import { useState, useEffect } from "react";

// Asset Imports
import logo from "../../assets/vodafone-logo-icon.svg";

// react hook form imports
import { useForm } from "react-hook-form";

// mui imports
import { Button } from "@mui/material";

// custom components imports
import InputField from "../input/input-component";
import OTPInput from "../otp-input/otp-input";
import { maskPhoneNumber } from "../../helpers/helpers";
import InlineSVG from "../inline-svg/inline-svg-component";
import WarningIcon from "../../assets/icons/solid/warning.svg";

import { login } from "../../services/auth.service";
import { sendOtp, validateOtp } from "../../services/checkout.service";

export default function SignIn({
  onClose,
  setAlertInfo,
  otpSent,
  setOtpSent,
  otpNumberOfAttempts,
  setOtpNumberOfAttempts,
  invalidOtp,
  setInvalidOtp,
  setTimer,
  timer,
}) {
  const { hostname } = window.location;

  const [loading, setLoading] = useState(false);
  const [cellPhoneNumberVerified, setCellPhoneNumberVerified] = useState(false);

  // Initialize phoneNumber with value from localStorage if it exists
  const [phoneNumber, setPhoneNumber] = useState(
    localStorage.getItem("phoneNumber") || ""
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    resetField,
    formState: { isDirty, errors },
  } = useForm();

  const watchCellPhoneNumber = watch("msisdn");
  const watchOtp = watch("otp");
  const maskedCellPhoneNumber = maskPhoneNumber(watchCellPhoneNumber);

  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 1) {
            clearInterval(countdown);
          }
          return prevTimer - 1;
        });
      }, 1000);
    }

    return () => clearInterval(countdown);
  }, [timer]);

  const handleOTPChange = (value) => {
    setValue("otp", value);
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pasteData = e.clipboardData.getData("text");
    const otpValue = pasteData.slice(0, 6);

    handleOTPChange(otpValue);
  };

  const resetAlertInfo = () => {
    setAlertInfo({
      showSignInAlert: false,
      signInAlertSeverity: "warning",
      signInAlertMessage: null,
    });
  };

  const onSubmit = async (data) => {
    setPhoneNumber(data.msisdn); // Store phone number in state
    localStorage.setItem("phoneNumber", data.msisdn); // Save to localStorage

    try {
      await sendOtp(data.msisdn, "login");
      setOtpSent(true);
      setTimer(90);
    } catch (error) {
      setTimer(0);
      setOtpSent(false);
      setAlertInfo({
        showSignInAlert: true,
        signInAlertSeverity: "warning",
        signInAlertMessage: (
          <div className="flex flex-col md:flex-row gap-1 md:gap-4 md:items-center">
            <div className="flex items-center gap-2">
              <div className="bg-white rounded-xl p-2">
                <InlineSVG
                  src={WarningIcon}
                  width={24}
                  height={24}
                  ariaHidden
                  className="fill-yellow-100"
                />
              </div>
              <h3 className="MuiAlert-message">
                This cellphone number is not registered. To register, please
                click "Cancel" to close this window and search your address to
                get you connected.
              </h3>
            </div>
          </div>
        ),
      });
    }
  };

  const handleLogin = async (data) => {
    console.log("data: ", data);
    if (!data.otp) {
      handleInvalidOtp();
      return;
    }

    try {
      const validOTP = await validateOtp(watchOtp, phoneNumber);

      if (validOTP) {
        setCellPhoneNumberVerified(true);
        await processLogin();
      } else {
        setValue("otp", ""); // This clears the OTP field visually
        resetField("otp"); // Reset the form state
        handleInvalidOtp();
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setAlertInfo({
        showSignInAlert: true,
        signInAlertSeverity: "warning",
        signInAlertMessage: (
          <div className="flex flex-col md:flex-row gap-1 md:gap-4 md:items-center">
            <div className="flex items-center gap-2">
              <div className="bg-white rounded-xl p-2">
                <InlineSVG
                  src={WarningIcon}
                  width={24}
                  height={24}
                  ariaHidden
                  className="fill-yellow-100"
                />
              </div>
              <h3 className="MuiAlert-message">
                This cellphone number is not registered. To register, please
                close this window and search your address to get you connected.
              </h3>
            </div>
          </div>
        ),
      });
      handleInvalidOtp();
    }
  };

  const handleInvalidOtp = () => {
    setOtpNumberOfAttempts((prevAttempts) => prevAttempts - 1);
    setInvalidOtp(true);

    setValue("otp", "");

    setAlertInfo({
      showSignInAlert: true,
      signInAlertSeverity: "warning",
      signInAlertMessage: (
        <div className="flex gap-4 items-center">
          <div className="bg-white rounded-xl p-2">
            <InlineSVG
              src={WarningIcon}
              width={24}
              height={24}
              ariaHidden
              className="fill-yellow-100"
            />
          </div>
          <div>
            <h3 className="MuiAlertTitle-root">
              Looks like you entered an invalid OTP
            </h3>
            <p className="MuiAlert-message">
              You have {otpNumberOfAttempts} attempts remaining. Please try
              again.
            </p>
          </div>
        </div>
      ),
    });
  };

  const processLogin = async () => {
    const response = await login(watchOtp, phoneNumber);
    if (response) {
      localStorage.setItem("token", JSON.stringify(response));

      // Clear phone number from localStorage on successful login
      localStorage.removeItem("phoneNumber");

      handleOnClose();
      window.location = "/fibre/prepaid/dashboard";
    } else {
      setAlertInfo({
        showSignInAlert: true,
        signInAlertSeverity: "warning",
        signInAlertMessage: "Something went wrong, please try again.",
      });
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    resetField("otp");
    resetAlertInfo();
  };

  const handleOnClose = () => {
    onClose();
    resetField("msisdn");
    resetAlertInfo();
    setOtpSent(false);
    setCellPhoneNumberVerified(false);
  };

  const handleResendOTP = async () => {
    setValue("otp", ""); // This clears the OTP field visually
    resetField("otp"); // Reset the form state
    if (timer === 0) {
      // Ensure it only runs if the timer is at 0
      if (!phoneNumber) {
        setAlertInfo({
          showSignInAlert: true,
          signInAlertSeverity: "error",
          signInAlertMessage:
            "Please enter your phone number before resending OTP.",
        });
        return;
      }

      try {
        await sendOtp(phoneNumber, "login");
        setTimer(90); // Set the timer to start countdown
        setOtpNumberOfAttempts(3); // Reset OTP attempts after resending
        resetAlertInfo(); // Reset any alert
      } catch (error) {
        console.error("Error resending OTP:", error);
        setAlertInfo({
          showSignInAlert: true,
          signInAlertSeverity: "error",
          signInAlertMessage: "Failed to resend OTP. Please try again.",
        });
      }
    }
  };

  return (
    <div className="w-full mx-auto flex flex-col gap-6 md:gap-10">
      <div className="w-full text-center flex flex-col align-center justify-center gap-4">
        {hostname !== "condorwebtests.link" && (
          <img
            src={logo}
            alt="Vodacom Logo"
            width={64}
            height={64}
            className="mx-auto mb-2 md:mt-4"
          />
        )}

        <h1 className="text-1xl md:text-2xl w-full font-semibold">
          Welcome to the Vodacom Prepaid Fibre Self-Service Portal
        </h1>

        {!otpSent ? (
          <p className="text-grey-700">
            Log in below to manage your prepaid fibre service
          </p>
        ) : (
          <p className="text-grey-600">
            We've sent a 6-digit one-time pin (OTP) to {maskedCellPhoneNumber}{" "}
            for secure access. Enter it below to continue:
          </p>
        )}
      </div>

      <form className="flex flex-col gap-6 md:gap-8">
        {!otpSent ? (
          <InputField
            type="text"
            className="w-full rounded-bl-none"
            inputLabel="Cellphone number"
            placeholder="Enter your cellphone number"
            InputProps={{
              ...register("msisdn", {
                required: "Cellphone number is required",
                pattern: {
                  value: /^0\d{9}$/,
                  message:
                    "Cellphone number must be 10 digits and start with a 0, e.g. 0821234567",
                },
              }),
            }}
            error={errors.msisdn ? true : false}
            helperText={errors.msisdn ? errors.msisdn.message : ""}
          />
        ) : (
          <div className="w-fit mx-auto md:w-full p-3 md:p-0 md:bg-transparent bg-grey-25 rounded-2xl md:rounded-none">
            <OTPInput
              autoFocus
              isNumberInput
              length={6}
              value={watchOtp || ""}
              onPaste={handlePaste}
              onChangeOTP={handleOTPChange}
            />
          </div>
        )}

        {/* <div className="w-fit mx-auto md:w-full p-3 md:p-0 md:bg-transparent bg-grey-25 rounded-2xl md:rounded-none">
          <OTPInput
            autoFocus
            isNumberInput
            length={6}
            value={watchOtp || ""}
            onChangeOTP={handleOTPChange}
          />
        </div> */}

        {otpSent && (
          <div className="flex md:hidden items-center justify-center my-2">
            <Button
              className="w-fit font-semibold"
              onClick={handleResendOTP}
              disabled={timer !== 0}
            >
              Resend OTP {timer > 0 && `(${timer}s)`}
            </Button>
          </div>
        )}

        <div className="flex flex-row-reverse md:flex-row items-center justify-center gap-4">
          {otpSent ? (
            <Button
              className="w-full md:w-auto"
              type="submit"
              variant="contained"
              disableFocusRipple
              disabled={
                loading || watchOtp?.length !== 6 || otpNumberOfAttempts <= 0
              }
              onClick={handleSubmit(handleLogin)}
            >
              Login
            </Button>
          ) : (
            <Button
              className="w-full md:w-auto"
              type="submit"
              variant="contained"
              disableFocusRipple
              disabled={!isDirty || loading || !watchCellPhoneNumber}
              onClick={handleSubmit(onSubmit)}
            >
              Send OTP
            </Button>
          )}

          <Button
            className="w-fit"
            variant="outlined"
            onClick={otpSent ? handleBack : handleOnClose}
          >
            {otpSent ? "Back" : "Cancel"}
          </Button>
        </div>
      </form>

      <div className="md:flex hidden items-center justify-between">
        <p className="font-medium text-grey-600">Didn't receive the OTP?</p>
        <Button
          className="w-fit font-semibold"
          onClick={handleResendOTP}
          disabled={timer > 0 || !otpSent}
        >
          Resend OTP {timer > 0 && `(${timer}s)`}
        </Button>
      </div>
    </div>
  );
}
